import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Backdrop from "@material-ui/core/Backdrop";
import { ClipLoader } from "react-spinners";
import moment from "moment";
import ReactTable from "react-table";
import Button from "../../Button";
import Snackbar from "../../Snackbar";
import {
  confirmationError,
  confirmationSuccess,
} from "../../../styles/common.css";

const useStyles = makeStyles((theme) => ({
  icons: {
    float: "right",
    "& > *": {
      margin: theme.spacing(1),
    },
    height: 25,
  },
  iconButtonLabel: {
    display: "flex",
    flexDirection: "column",
  },
  assignMeterDialog: {
    minHeight: "400px",
    minWidth: "1200px",
  },
  searchMeterResult: {
    marginLeft: 15,
  },
  createTransaction: {
    "&  *": {
      marginRight: "20px",
      width: "200px",
    },
  },
  input175: {
    marginRight: "20px",
    width: "100%",
  },
  input200: {
    marginRight: "20px",
    width: "175px",
  },
  transactionDetailRoot: {
    width: "100%",
    height: 420,

    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      width: "100%",
      margin: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
  },
  transactionDetailHeader: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(4),
    display: "inline-block",
    fontWeight: theme.typography.fontWeightRegular,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: "33.33%",
    flexShrink: 0,
  },
  secondaryHeading: {
    display: "inline-block",
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
    marginLeft: 10,
  },
  divider: {
    margin: "20px 0",
  },
  editTransactionView: {
    height: 550,
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "100%",
    },
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  label: {
    fontSize: ".75rem",
    fontWeight: 400,
    lineHeight: 1,
  },
  formControl: {
    width: "100%",
    paddingLeft: 10,
  },
  dialog: {
    position: "absolute",
    scroll: "none",
    top: 0,
  },
  transactionData: {
    display: "flex",
    flexDirection: "column",
    padding: "15px",
    justifyContent: "space-between",
  },
  transactionInfo: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    fontSize: "18px",
    justifyContent: "space-between",
  },
  transactionBody: {
    height: "100%",
    marginTop: "20px",
    border: "1px solid #ddd",
  },
  transactionHistoryDialog: {
    position: "absolute",
    scroll: "none",
    top: "10vh",
  },
  transactionProcess: {
    fontSize: "12px",
    marginBottom: "5px"
  },
  transactionHistory: {
    display: "flex",
    flexDirection: "column",
    fontSize: "18px",
    padding: "15px",
    marginBottom: "20px",
    border: "1px solid #ddd",
    height: "400px",
    overflowY: "scroll",
    "& h1": {
      fontSize: "19px",
      textDecoration: "underline",
      paddingBottom: "15px",
      margin: 0,
      display: "flex",
      justifyContent: "space-between",
      "& .MuiSvgIcon-root": {
        color: "#3f51b5",
        transition: "0.15s all linear",
        "&:hover": {
          transform: "scale(1.05)",
        },
      },
    },
  },
  tableHeaderRow: {
    backgroundColor: "#333",
    "& .MuiTableCell-root": {
      fontSize: "15px",
      color: "white",
    },
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'orange',
    height: '50px',
    color: 'black',
    width: '100%',
    fontSize: '16px',
    fontWeight: 'bolder'
  }
}));

const PaymentList = ({
  transactions,
  showSnackBar,
  successMessage,
  errorMessage,
  total,
  has_next,
  fetchAllMeterTransactions,
  closeSnackbar,
  isProcessing,
  selectedView,
  tdsps,
  fetchTdsps,
}) => {
  const classes = useStyles();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const fetchData = (page) => {
    fetchAllMeterTransactions(page, [], "", { includeTransactionData: false, transactionTypeName: "820", include_display_columns: true });
  };

  const getDate = (value) => {
    if (!value) {
      return "";
    }
    const d = value.split("T")[0];
    return moment(d).format("YYYY-MM-DD");
  };

  const getTime = (value) => {
    if (!value) {
      return ""
    } else {
      const hours = value.substring(0, 2)
      const mins = value.substring(2, 4)
      const secs = value.substring(4, 6)
      const formatted_time = `${hours}:${mins}:${secs}`
      return formatted_time
    }
  }

  const wildcardFilter = (filter, row) =>
    row[filter.id].toLowerCase().indexOf(filter.value.toLowerCase()) !== -1;

  const columns = [
    {
      Header: "ID",
      accessor: "intId",
      show: false,
    },
    {
      Header: "Date",
      accessor: "transactionDate",
      width: 80,
      Cell: (row) => getDate(row.value),
    },
    {
      Header: "Time",
      accessor: "transaction_time",
      width: 100,
      Cell: (row) => getTime(row.value),
    },
    {
      Header: "TDSP",
      accessor: "duns",
      Cell: (row) => {
        const tdsp = tdsps.find((t) => t.duns_number === row.value);
        return tdsp ? tdsp.name : row.value;
      },
    },
    {
      Header: "Type",
      accessor: "transactionTypeName",
      filterMethod: wildcardFilter,
    },
    {
      Header: "Reason Status",
      accessor: "transactionSubType",
      filterMethod: wildcardFilter,
    },
    {
      Header: "Sender Trans ID",
      accessor: "senderTransactionID",
    },
    {
      Header: "Status",
      accessor: "processingStatus",
      filterMethod: wildcardFilter,
    },
    {
      Header: "Transaction Handling Code",
      accessor: "transaction_handling_code",
    },
    {
      Header: "Total Distribution Amount",
      accessor: "total_distribution_amount",
    },
    {
      Header: "Payment Method",
      accessor: "payment_method",
    },
    {
      Header: "Trace Number",
      accessor: "trace_number",
    },
    {
      Header: "Credit Or Debit Code",
      accessor: "credit_or_debit_code",
    },
    {
      Header: "Payer Intended Settlement Date",
      accessor: "payer_intended_settlement_date",
    },
  ];

  const filterTransactions = (transactions) =>
    transactions.map((tran) => ({ ...tran, intId: parseInt(tran.id) }));

  const handlePageChange = (pageIndex) => {
    setPageIndex(pageIndex);
  };

  const handlePageSizeChange = (pageSize) => {
    setPageSize(pageSize);
  };

  useEffect(() => {
    const START_PAGE = 1;
    if (!tdsps || tdsps.length === 0) {
      fetchTdsps();
    }
    fetchData(START_PAGE);
  }, [selectedView]);

  return (
    <>
      <ReactTable
        data={filterTransactions(transactions)}
        columns={columns}
        className="-striped -highlight"
        defaultPageSize={10}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        filterable
        defaultSorted={[
          {
            id: "intId",
            desc: true,
          },
        ]}
      />
      <Snackbar
        id="message-id"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        open={showSnackBar}
        onClose={closeSnackbar}
        className={successMessage ? confirmationSuccess : confirmationError}
        message={successMessage || errorMessage}
      />
      {has_next && (
        <Button
          onClick={async () => {
            await fetchData();
          }}
          color="primary"
          variant="contained"
          style={{
            marginRight: 20,
            marginTop: 10,
          }}
        >
          Load More
        </Button>
      )}
      <div style={{ float: "right", marginTop: "10px" }}>
        Downloaded {transactions.length} of {total} payments
      </div>
      <Backdrop className={classes.backdrop} open={isProcessing}>
        <ClipLoader sizeUnit="px" size={100} color="#ED0874" loading />
      </Backdrop>
    </>
  );
};

PaymentList.propTypes = {
  closeSnackbar: PropTypes.any,
  errorMessage: PropTypes.any,
  fetchAllMeterTransactions: PropTypes.func,
  fetchMeterTransactions: PropTypes.func,
  params: PropTypes.shape({
    memberId: PropTypes.any,
  }),
  processTransaction: PropTypes.func,
  selectedIndex: PropTypes.any,
  setSelectedTransaction: PropTypes.func,
  showSnackBar: PropTypes.any,
  successMessage: PropTypes.any,
  transactionIsModified: PropTypes.any,
  transactions: PropTypes.array,
  updateTransaction: PropTypes.func,
};

export default PaymentList;
